import React, { useEffect, useMemo, useState } from 'react';
import caseService from '../../services/caseService';
import './dataEditor.css';

// Self-contained JsonEditor implementation and styles
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = (v) => Array.isArray(v);
const extendedTypeOf = (v, hint) => {
  if (hint) return hint;
  if (isArray(v)) return 'array';
  if (isObject(v)) return 'object';
  if (typeof v === 'number' && isFinite(v)) return 'number';
  const s = v == null ? '' : String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date';
  if (s.includes('\n') || s.length > 120) return 'textArea';
  return 'string';
};
const todayStr = () => { const d = new Date(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; };
const defaultForType = (t) => { switch (t) { case 'array': return []; case 'object': return {}; case 'number': return 0; case 'date': return todayStr(); case 'textArea': return ''; case 'string': default: return ''; } };
const convertValueToType = (val, t) => {
  switch (t) {
    case 'array': return Array.isArray(val) ? val : [];
    case 'object': return isObject(val) ? val : {};
    case 'number': { if (typeof val === 'number' && isFinite(val)) return val; const n = parseFloat(String(val ?? '').trim()); return Number.isFinite(n) ? n : 0; }
    case 'date': { const s = String(val ?? '').trim(); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d = new Date(s); if (!isNaN(d.getTime())) { const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; } return todayStr(); }
    case 'textArea':
    case 'string':
    default: return String(val ?? '');
  }
};
const deepClone = (v) => { try { return JSON.parse(JSON.stringify(v)); } catch (_) { return defaultForType(extendedTypeOf(v)); } };
const TypeSelector = ({ value, onChange, typeHint, disabled }) => {
  const t = extendedTypeOf(value, typeHint);
  return (
    <select className="form-select form-select-sm" value={t} onChange={(e) => onChange(convertValueToType(value, e.target.value))} disabled={!!typeHint || disabled}>
      <option value="string">string</option>
      <option value="textArea">textArea</option>
      <option value="number">number</option>
      <option value="date">date</option>
      <option value="array">array</option>
      <option value="object">object</option>
    </select>
  );
};
const StringEditor = ({ value, onChange }) => (
  <input type="text" className="form-control form-control-sm" value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const TextAreaEditor = ({ value, onChange }) => (
  <textarea className="form-control form-control-sm" rows={4} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const NumberEditor = ({ value, onChange }) => (
  <input type="number" step="any" className="form-control form-control-sm" value={typeof value === 'number' && isFinite(value) ? value : 0} onChange={(e) => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? n : 0); }} />
);
const DateEditor = ({ value, onChange }) => { const toDateVal = (v) => { const s = String(v ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); }; return (<input type="date" className="form-control form-control-sm" value={toDateVal(value)} onChange={(e) => onChange(e.target.value)} />); };
const ObjectEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const entries = Object.entries(value || {});
  const renameKey = (oldKey, newKey) => { if (lockSchema && level === 0) return; if (!newKey || newKey === oldKey) return; const exists = Object.prototype.hasOwnProperty.call(value, newKey); const safeKey = exists ? `${newKey}_1` : newKey; const next = { ...value }; next[safeKey] = next[oldKey]; delete next[oldKey]; onChange(next); };
  const updateChild = (key, nextVal) => { onChange({ ...value, [key]: nextVal }); };
  const removeKey = (key) => { if (lockSchema && level === 0) return; const next = { ...value }; delete next[key]; onChange(next); };
  const addField = () => { if (lockSchema && level === 0) return; let i = entries.length + 1; let key = `key${i}`; while (Object.prototype.hasOwnProperty.call(value, key)) { i += 1; key = `key${i}`; } onChange({ ...value, [key]: '' }); };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {entries.length === 0 && <div className="text-muted small">(empty object)</div>}
      {entries.map(([k, v], idx) => (
        <div key={`${k}-${idx}`} className="json-field-row d-flex align-items-start gap-2 mb-2">
          <input className="form-control form-control-sm json-key-input" value={k} onChange={(e) => renameKey(k, e.target.value)} readOnly={lockSchema && level === 0} />
          <div className="json-type"><TypeSelector value={v} onChange={(nv) => updateChild(k, nv)} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} /></div>
          <div className="json-value flex-grow-1"><ValueEditor value={v} onChange={(nv) => updateChild(k, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} /></div>
          {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeKey(k)}>Delete</button>)}
        </div>
      ))}
      {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addField}>+ Add field</button>)}
    </div>
  );
};
const ArrayEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (index, nextVal) => { const next = items.slice(); next[index] = nextVal; onChange(next); };
  const removeItem = (index) => { const next = items.slice(); next.splice(index, 1); onChange(next); };
  const addItem = () => { onChange([...(items || []), '']); };
  return (
    <div className="json-array editor-block border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small">(empty array)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
          <div className="json-type"><TypeSelector value={it} onChange={(nv) => updateItem(idx, nv)} /></div>
          <div className="json-value flex-grow-1"><ValueEditor value={it} onChange={(nv) => updateItem(idx, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} /></div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeItem(idx)}>Delete</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ Add item</button>
    </div>
  );
};
const ValueEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema, typeHint }) => {
  const t = extendedTypeOf(value, typeHint);
  if (t === 'object') return <ObjectEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'array') return <ArrayEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (t === 'date') return <DateEditor value={value} onChange={onChange} />;
  if (t === 'textArea') return <TextAreaEditor value={value} onChange={onChange} />;
  return <StringEditor value={value} onChange={onChange} />;
};
// Content array editor for News/Cases content field
const ContentArrayEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  
  const updateItem = (index, nextVal) => {
    const next = items.slice();
    next[index] = nextVal;
    onChange(next);
  };
  
  const removeItem = (index) => {
    const next = items.slice();
    next.splice(index, 1);
    onChange(next);
  };
  
  const addItem = (type = 'text') => {
    onChange([...items, { type, content: '' }]);
  };
  
  const moveItem = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const next = items.slice();
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      onChange(next);
    } else if (direction === 'down' && index < items.length - 1) {
      const next = items.slice();
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      onChange(next);
    }
  };

  return (
    <div className="content-array-editor border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small mb-2">(空內容數組)</div>}
      {items.map((item, idx) => {
        const itemValue = item && typeof item === 'object' ? item : { type: 'text', content: String(item || '') };
        const itemType = itemValue.type || 'text';
        const itemContent = itemValue.content || '';
        
        return (
          <div key={idx} className="content-item-row border rounded p-2 mb-2 bg-white">
            <div className="d-flex align-items-start gap-2 mb-2">
              <div className="d-flex flex-column gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveItem(idx, 'up')}
                  disabled={idx === 0}
                  title="向上移動"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveItem(idx, 'down')}
                  disabled={idx === items.length - 1}
                  title="向下移動"
                >
                  ↓
                </button>
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: '120px' }}
                value={itemType}
                onChange={(e) => updateItem(idx, { ...itemValue, type: e.target.value })}
              >
                <option value="text">文字</option>
                <option value="image">圖片</option>
              </select>
              <div className="flex-grow-1">
                {itemType === 'text' ? (
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={itemContent}
                    onChange={(e) => updateItem(idx, { ...itemValue, content: e.target.value })}
                    placeholder="輸入文字內容..."
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={itemContent}
                    onChange={(e) => updateItem(idx, { ...itemValue, content: e.target.value })}
                    placeholder="輸入圖片網址..."
                  />
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeItem(idx)}
              >
                刪除
              </button>
            </div>
            {itemType === 'image' && itemContent && (
              <div className="mt-2">
                <img src={itemContent} alt="預覽" style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
        );
      })}
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem('text')}>+ 新增文字</button>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem('image')}>+ 新增圖片</button>
      </div>
    </div>
  );
};

// Fixed item editor for Cases with locked schema
const FixedItemEditor = ({ value, onChange }) => {
  const v = value || {};
  const id = Number.isFinite(v.id) ? v.id : parseInt(v.id, 10) || 0;
  const title = v.title ?? '';
  const date = (() => { const s = String(v.date ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); })();
  const excerpt = v.excerpt ?? '';
  const content = Array.isArray(v.content) ? v.content : (v.content ? [{ type: 'text', content: String(v.content) }] : []);
  const image = v.image ?? '';

  const patch = (p) => onChange({ ...v, ...p, id });

  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">ID</span>
        <div className="json-value flex-grow-1 d-flex align-items-center" aria-label="ID value">
          <span className="form-text">{id}</span>
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">日期</span>
        <div className="json-value flex-grow-1">
          <input type="date" className="form-control form-control-sm" value={date} onChange={(e) => patch({ date: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">摘要</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={excerpt} onChange={(e) => patch({ excerpt: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">內容</span>
        <div className="json-value flex-grow-1">
          <ContentArrayEditor value={content} onChange={(newContent) => patch({ content: newContent })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">圖片</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={image} onChange={(e) => patch({ image: e.target.value })} />
        </div>
      </div>
    </div>
  );
};

const LocalJsonEditor = ({ title, initialValue, onSave, filename, fieldTypeHints, lockSchema = false }) => {
  const [value, setValue] = useState(() => deepClone(initialValue));
  const [savedOnce, setSavedOnce] = useState(false);
  useEffect(() => { setValue(deepClone(initialValue)); setSavedOnce(false); }, [initialValue]);
  
  // Check if form data has changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(value) !== JSON.stringify(initialValue);
  }, [value, initialValue]);
  
  const save = async () => {
    try {
      if (onSave) await onSave(deepClone(value));
      setSavedOnce(true);
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (_) { setSavedOnce(false); }
  };
  return (
    <div className="json-editor card shadow-sm mb-3">
      <div className="card-body">
        <FixedItemEditor value={value} onChange={setValue} />
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-3 border-top">
          {savedOnce && <div className="text-success small me-2">已儲存。</div>}
          <button 
            type="button" 
            className="btn btn-sm btn-primary" 
            onClick={save}
            disabled={!hasChanges}
          >
            儲存變更
          </button>
        </div>
      </div>
    </div>
  );
};
// Wrapper: loads list, lets user pick an item to edit, saves via caseService
const CasesDataEditor = ({ filename }) => {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'deleted'
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const itemsPerPage = 10;

  const refresh = async (keepSelected = true) => {
    const l = await caseService.getListData();
    setList(Array.isArray(l) ? l : []);
    if (!keepSelected) {
      const filtered = getFilteredList(Array.isArray(l) ? l : []);
      if (filtered.length > 0) setSelectedId(String(filtered[0].id));
      else setSelectedId('');
    }
  };

  const getFilteredList = (items) => {
    if (filter === 'active') return items.filter(it => !it.isDeleted);
    if (filter === 'deleted') return items.filter(it => it.isDeleted);
    return items;
  };

  useEffect(() => {
    (async () => { await refresh(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredList = useMemo(() => getFilteredList(list), [list, filter]);
  
  // Pagination logic
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedList = useMemo(() => filteredList.slice(startIndex, endIndex), [filteredList, startIndex, endIndex]);
  
  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Update selectedId when filter changes if current selection is not in filtered list
  useEffect(() => {
    if (selectedId && !filteredList.find(it => String(it.id) === String(selectedId))) {
      if (filteredList.length > 0) {
        setSelectedId(String(filteredList[0].id));
      } else {
        setSelectedId('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, filteredList]);

  const current = useMemo(() => {
    if (!selectedId) return null;
    // If selectedId is 'new', return a new empty item
    if (selectedId === 'new') {
      return { title: '', date: todayStr(), excerpt: '', content: [], image: '', isDeleted: false };
    }
    return (list || []).find((it) => String(it.id) === String(selectedId)) || null;
  }, [selectedId, list]);

  const handleSave = async (next) => {
    if (!selectedId) return;
    // If selectedId is 'new', insert as new item
    if (selectedId === 'new') {
      const inserted = await caseService.insertObjectToArrayData(next || {});
      await refresh(true);
      if (inserted && inserted.id != null) {
        setSelectedId(String(inserted.id));
      } else {
        setSelectedId('');
      }
    } else {
    await caseService.updateListObjectDataById(selectedId, next || {});
    await refresh(true);
    }
    setShowEditModal(false);
  };
  
  const handleRowClick = (id) => {
    setSelectedId(String(id));
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    // Reset selectedId if it was 'new' (user cancelled creating new item)
    if (selectedId === 'new') {
      setSelectedId('');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) return;
    try {
      await caseService.deleteObjectInArrayData(id);
      await refresh(true);
      // If deleted item was selected, select first available item
      const filtered = getFilteredList(list.filter(it => String(it.id) !== String(id)));
      if (filtered.length > 0) setSelectedId(String(filtered[0].id));
      else setSelectedId('');
      // Close modal if the deleted item was being edited
      if (String(selectedId) === String(id)) {
        setShowEditModal(false);
        setSelectedId('');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed');
    }
  };
  
  const handleToggleStatus = async (id) => {
    const item = list.find(it => String(it.id) === String(id));
    if (!item) return;
    const newStatus = !item.isDeleted;
    try {
      await caseService.updateListObjectDataById(id, { isDeleted: newStatus });
      await refresh(true);
    } catch (error) {
      console.error('Toggle status failed:', error);
      alert('Toggle status failed');
    }
  };

  const title = useMemo(() => {
    const base = '案例數據';
    if (!current) return base;
    const label = current.title || current.label || '';
    return `${base} — 項目 ${current.id}${label ? ` (${label})` : ''}`;
  }, [filename, current]);


  return (
    <div>
      <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => {
            setSelectedId('new');
            setShowEditModal(true);
          }}
        >
          + 新增案例
        </button>
        <div className="btn-group" role="group">
          <input
            type="radio"
            className="btn-check"
            name="filter"
            id="filter-all"
            checked={filter === 'all'}
            onChange={() => setFilter('all')}
          />
          <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-all">全部</label>
          
          <input
            type="radio"
            className="btn-check"
            name="filter"
            id="filter-active"
            checked={filter === 'active'}
            onChange={() => setFilter('active')}
          />
          <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-active">啟用</label>
          
          <input
            type="radio"
            className="btn-check"
            name="filter"
            id="filter-deleted"
            checked={filter === 'deleted'}
            onChange={() => setFilter('deleted')}
          />
          <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-deleted">已刪除</label>
        </div>
      </div>
      {/* Table view */}
      <div className="table-responsive">
        <table className="table table-sm table-bordered table-hover">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>ID</th>
              <th>標題</th>
              <th style={{ width: '120px' }}>日期</th>
              <th style={{ width: '100px' }}>狀態</th>
              <th style={{ width: '220px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedList.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  未找到案例
                </td>
              </tr>
            ) : (
              paginatedList.map((item) => (
                <tr 
                  key={item.id} 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(item.id)}
                >
                  <td>{item.id}</td>
                  <td>{item.title || '(無標題)'}</td>
                  <td>{item.date || '-'}</td>
                  <td>
                    <span className={`badge ${item.isDeleted ? 'bg-secondary' : 'bg-success'}`}>
                      {item.isDeleted ? '已刪除' : '啟用'}
                    </span>
                  </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className={`btn btn-sm ${item.isDeleted ? 'btn-outline-success' : 'btn-outline-warning'}`}
                            onClick={() => handleToggleStatus(item.id)}
                            title={item.isDeleted ? '啟用此項目' : '停用此項目'}
                          >
                            {item.isDeleted ? '啟用' : '停用'}
                          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(item.id)}
          >
                            刪除
          </button>
                        </div>
                      </td>
                </tr>
              ))
        )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (() => {
        // Calculate which page numbers to show
        const getPageNumbers = () => {
          const pages = [];
          const maxVisible = 7; // Maximum number of page buttons to show
          
          if (totalPages <= maxVisible) {
            // Show all pages if total pages is less than maxVisible
            for (let i = 1; i <= totalPages; i++) {
              pages.push(i);
            }
          } else {
            // Show pages with ellipsis
            if (currentPage <= 3) {
              // Show first pages
              for (let i = 1; i <= 5; i++) {
                pages.push(i);
              }
              pages.push('ellipsis');
              pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
              // Show last pages
              pages.push(1);
              pages.push('ellipsis');
              for (let i = totalPages - 4; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              // Show middle pages
              pages.push(1);
              pages.push('ellipsis');
              for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                pages.push(i);
              }
              pages.push('ellipsis');
              pages.push(totalPages);
            }
          }
          return pages;
        };
        
        const pageNumbers = getPageNumbers();
        
        return (
          <nav aria-label="Cases pagination" className="mt-3">
            <ul className="pagination pagination-sm justify-content-center">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  上一頁
                </button>
              </li>
              {pageNumbers.map((page, idx) => {
                if (page === 'ellipsis') {
                  return (
                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                return (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  下一頁
                </button>
              </li>
            </ul>
          </nav>
        );
      })()}
      
      {/* Edit Modal */}
      {showEditModal && current && (
        <>
          <div 
            className="modal fade show" 
            style={{ display: 'block', zIndex: 1050 }} 
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseModal();
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
        <LocalJsonEditor
          title={title}
          initialValue={current}
          filename={filename || 'casesData.json'}
          onSave={handleSave}
        />
                </div>
              </div>
            </div>
          </div>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 1040 }}
            onClick={handleCloseModal}
          ></div>
        </>
      )}
    </div>
  );
};

export default CasesDataEditor;
